/**
 * Giao dien dat cuoc tren BANG KET QUA NHIEU GIAI: Lô, Đề, Chẵn lẻ, Tài xỉu.
 *
 * Bon dieu man hinh nay phai lam duoc, va deu la thu quyet dinh nguoi choi co
 * hieu minh dang mua gi hay khong:
 *
 *   1. HIEN SO HAI CHU SO. Cuoc dat tren "07" chu khong phai tren "7". In ra 7
 *      thi luoi so lech hang va nguoi choi phai tu doan.
 *   2. NOI DIEU KIEN THANG bang mot cau, ngay canh cho bam. Boi so khong noi
 *      duoc gi neu khong biet trung nghia la gi — va Lô voi Đề trung theo hai
 *      luat khac han nhau tren cung mot con so.
 *   3. QUY BOI SO RA TIEN. "×95" la con so tru tuong; "10.000 → 950.000" thi
 *      khong. Nguoi choi doi chieu bang con so thu hai.
 *   4. CANH BAO TRAN CHI TRA TRUOC KHI TRA TIEN. Boi so 95 lan tren muc cuoc
 *      lon khong cho ra 95 lan, vi tran chi tra moi ve cat truoc (LOT-R33).
 *      Noi sau khi trung la mot vu khieu nai.
 *
 * Chon NHIEU SO mot luot la co chu dinh: dat lo de thuong la dat mot lo so cung
 * mot muc tien. Bat them tung so mot, moi so mot lan bam "Thêm dòng", la bat lam
 * lai mot thao tac ma nguoi choi coi la MOT hanh dong.
 */

import { type JSX } from 'react';
import type {
  LotteryBoardConfig,
  LotteryGroupMarket,
  LotteryPlayType,
  LotteryPrizeTier,
  LotteryResultCell,
} from '../lib/api';
import { formatMoney } from '../lib/money';
import styles from './LotteryBoard.module.css';

/** Nhan va cau giai thich cho tung kieu choi tren bang. */
export const BOARD_PLAY_TYPES: Readonly<
  Record<string, { label: string; short: string }>
> = {
  DE: { label: 'Đề', short: 'Hai số cuối giải đặc biệt' },
  LO: { label: 'Lô', short: 'Hai số cuối của bất kỳ giải nào' },
  GROUP: { label: 'Chẵn lẻ · Tài xỉu', short: 'Đặt theo tính chất của số' },
};

/** So hai chu so luon hien du hai chu so: 7 phai doc la "07". */
export function padNumber(value: number, digits: number): string {
  return String(value).padStart(digits, '0');
}

function multiplierText(bp: number): string {
  return `×${(bp / 10_000).toLocaleString('vi-VN', { maximumFractionDigits: 2 })}`;
}

/** Quy boi so ra so tien thuc nhan tren mot muc cuoc. */
function payoutOf(stakeMinor: bigint, bp: number): bigint {
  return (stakeMinor * BigInt(bp)) / 10_000n;
}

// ---------------------------------------------------------------------------

/**
 * Luat thang cua tung cua cuoc nhom, dien giai tu CAU HINH chu khong viet cung.
 *
 * Cau hinh doi duoc so chu so cua cua cuoc; neu cau nay viet cung "từ 5 đến 9"
 * thi mot lan doi cau hinh se bien no thanh mot cau sai ma khong ai phat hien.
 */
export function outcomeRule(market: LotteryGroupMarket, code: string): string {
  const range = 10 ** market.digits;
  const half = range / 2;
  const top = padNumber(range - 1, market.digits);
  const mid = padNumber(half, market.digits);
  const beforeMid = padNumber(half - 1, market.digits);
  const zero = padNumber(0, market.digits);

  switch (code) {
    case 'CHAN':
      return 'Thắng khi số là số chẵn.';
    case 'LE':
      return 'Thắng khi số là số lẻ.';
    case 'TAI':
      return `Thắng khi số nằm từ ${mid} đến ${top}.`;
    case 'XIU':
      return `Thắng khi số nằm từ ${zero} đến ${beforeMid}.`;
    default:
      return '';
  }
}

/** Cau mo ta cua cuoc lay so o dau. */
export function marketSource(market: LotteryGroupMarket, board: LotteryBoardConfig): string {
  const label = board.positions[market.prize_index]?.label ?? `giải thứ ${market.prize_index + 1}`;
  const which = market.digits === 1 ? 'chữ số cuối cùng' : `${market.digits} chữ số cuối`;
  return `Tính trên ${which} của ${label.toLowerCase()}.`;
}

// ---------------------------------------------------------------------------

/**
 * Luoi 00–99.
 *
 * Chon nhieu so cung luc, moi so thanh mot dong cuoc rieng o cung muc tien.
 * Khong khoa so o tren cung nhu luoi Keno: o day khong co gioi han so luong,
 * gioi han that nam o tong tien va o tran chi tra.
 */
export function NumberGrid({
  min,
  max,
  digits,
  picked,
  onChange,
}: {
  min: number;
  max: number;
  digits: number;
  picked: number[];
  onChange: (update: (prev: number[]) => number[]) => void;
}): JSX.Element {
  const numbers = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  return (
    <>
      <div className={styles.grid}>
        {numbers.map((n) => {
          const on = picked.includes(n);
          return (
            <button
              key={n}
              type="button"
              className={[styles.cell, on ? styles.cellOn : ''].filter(Boolean).join(' ')}
              // Cap nhat theo dang ham: nhieu cu bam sat nhau tren dien thoai
              // roi vao cung mot nhip dung hinh, doc gia tri cu thi cu sau ghi
              // de cu truoc va mot lua chon lang le bien mat.
              onClick={() =>
                onChange((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]))
              }
              aria-pressed={on}
            >
              {padNumber(n, digits)}
            </button>
          );
        })}
      </div>

      <div className={styles.gridFoot}>
        <span className={styles.count}>
          Đã chọn <strong className="numeric">{picked.length}</strong> số
        </span>
        <div className={styles.gridActions}>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => onChange(() => pickRandom(numbers, 5))}
          >
            Chọn nhanh 5 số
          </button>
          <button
            type="button"
            className={styles.linkButton}
            disabled={picked.length === 0}
            onClick={() => onChange(() => [])}
          >
            Xóa hết
          </button>
        </div>
      </div>
    </>
  );
}

function pickRandom(pool: readonly number[], count: number): number[] {
  const copy = [...pool];
  const out: number[] = [];
  while (out.length < count && copy.length > 0) {
    out.push(copy.splice(Math.floor(Math.random() * copy.length), 1)[0]!);
  }
  return out.sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------

/**
 * Cac cua cuoc nhom.
 *
 * Moi cua mot khoi rieng, kem cau noi ro lay so o dau. Hai cua cung ten "Tài"
 * tren hai khoi khac nhau la hai cuoc khac nhau — gop chung mot hang nut thi
 * khong the phan biet.
 */
export function GroupMarkets({
  board,
  selected,
  stakeMinor,
  currency,
  onSelect,
}: {
  board: LotteryBoardConfig;
  selected: string | null;
  stakeMinor: bigint;
  currency: string;
  onSelect: (selection: string) => void;
}): JSX.Element {
  return (
    <div className={styles.markets}>
      {board.group_markets.map((market) => (
        <div key={market.code} className={styles.market}>
          <div className={styles.marketHead}>
            <span className={styles.marketName}>{market.name}</span>
            <span className={styles.marketSource}>{marketSource(market, board)}</span>
          </div>

          <div className={styles.outcomes}>
            {market.outcomes.map((outcome) => {
              const value = `${market.code}:${outcome.code}`;
              const on = selected === value;
              return (
                <button
                  key={outcome.code}
                  type="button"
                  className={[styles.outcome, on ? styles.outcomeOn : ''].filter(Boolean).join(' ')}
                  onClick={() => onSelect(value)}
                  aria-pressed={on}
                >
                  <span className={styles.outcomeName}>{outcome.name}</span>
                  <span className={`numeric ${styles.outcomeOdds}`}>
                    {multiplierText(outcome.multiplier_bp)}
                  </span>
                  <span className={styles.outcomeRule}>{outcomeRule(market, outcome.code)}</span>
                  {stakeMinor > 0n && (
                    <span className={`numeric ${styles.outcomePayout}`}>
                      nhận{' '}
                      {formatMoney({
                        minor: payoutOf(stakeMinor, outcome.multiplier_bp),
                        currency,
                      })}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Bang thuong cua Lô va Đề.
 *
 * Bac cuoi cua Lô ghi "trở lên", khong ghi mot con so tron. Do la su that: may
 * chu tra theo huong don dieu, nen trung nhieu lan hon bac cuoi van nhan dung
 * bac cuoi chu khong roi ve 0. Ghi "5 nháy" tron thi nguoi trung 6 nhay se
 * tuong minh bi tra thieu.
 */
export function BoardPrizeTable({
  playType,
  tiers,
  stakeMinor,
  currency,
}: {
  playType: LotteryPlayType;
  tiers: LotteryPrizeTier[];
  stakeMinor: bigint;
  currency: string;
}): JSX.Element | null {
  if (tiers.length === 0) return null;

  const sorted = [...tiers].sort((a, b) => a.matched - b.matched);
  const last = sorted[sorted.length - 1]!;

  return (
    <table className={styles.prizeTable}>
      <thead>
        <tr>
          <th>Kết quả</th>
          <th>Bội số</th>
          {stakeMinor > 0n && <th>Nhận được</th>}
        </tr>
      </thead>
      <tbody>
        {sorted.map((tier) => (
          <tr key={tier.tier}>
            <td>
              {playType === 'DE'
                ? 'Trúng'
                : tier === last
                  ? `${tier.matched} nháy trở lên`
                  : `${tier.matched} nháy`}
            </td>
            <td className="numeric">{multiplierText(tier.multiplier_bp)}</td>
            {stakeMinor > 0n && (
              <td className="numeric">
                {formatMoney({ minor: payoutOf(stakeMinor, tier.multiplier_bp), currency })}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------

/**
 * Bang ket qua da cong bo, kem danh dau nhung giai ma so cua nguoi choi khop.
 *
 * Day la man hinh nguoi choi mo ra khi khong dong y voi ket qua. No phai tra
 * loi duoc cau hoi that su cua ho — so cua ho co tren bang khong, va o giai nao
 * — chu khong chi bao "Khong trung".
 */
export function ResultBoard({
  cells,
  highlight,
}: {
  cells: LotteryResultCell[];
  highlight: readonly number[];
}): JSX.Element {
  const hit = new Set(highlight);

  return (
    <div className={styles.board}>
      {cells.map((cell, index) => (
        <div
          key={index}
          className={[styles.prize, hit.has(index) ? styles.prizeHit : ''].filter(Boolean).join(' ')}
        >
          <span className={styles.prizeLabel}>{cell.label}</span>
          <span className={`numeric ${styles.prizeValue}`}>
            {cell.value === null ? '—' : padNumber(cell.value, cell.digits)}
          </span>
        </div>
      ))}
    </div>
  );
}
